// List ACTIONS + row click for the four status-scoped Components grids
// (/components/flying, removed, rotable, retired) — H100.
//
// Rule 0: Blank's list shape (src/blank/BlankList.tsx) — an Edit entry that
// opens the `:id` record, and a row click that opens the same record. The
// record lives at /components/items/:id, a SIBLING of the mode routes, so the
// navigate goes `..` first (BlankList's onRowClick comment; the same trap).
//
// Legacy per-item row actions (mod_parts/js/parts_display.js, handler
// parts_ajax_handler.inc:1320-1500) and what v6 offers for each:
//   Edit Item (js 526-529)            -> "Edit Component" / "View Component"
//   Print Label + View Label PDF      -> "Print Label" (the v6 inventory_label
//     (js 531-539, 550-552)              stationery, shared with Inventory Stock)
//   Serial # -> S/N report (555-557)  -> NOT BUILT: no serial-number report in v6
//   Split Item (541-548)              -> NOT BUILT (and legacy offers it only at
//                                         status 4/16/17/18, never on Flying)
//   Delete Item (630-635)             -> NOT BUILT: legacy gates it on parts_delete
//                                         AND a lone "New Item Added" adjustment
//   Merge Components (parts_nav 82)   -> NOT BUILT: no v6 merge route
// A NOT BUILT entry is left out rather than shown disabled: the canonical
// ActionMenu does not render `disabledReason`, so a greyed entry would carry no
// explanation (ActionMenu.tsx, disabledReason doc).
import { useCallback, useMemo, useState } from "react";
import { GridRowParams, MuiEvent } from "@mui/x-data-grid-pro";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

import LoadingSpinner from "../../components/Loading";
import PdfInlinePreviewDialog from "../../components/pdf/PdfInlinePreviewDialog";
import type { ActionMenuItem } from "../../components/datagrid/ActionMenu";
import { useGlobalConfig } from "../../hooks/useGlobalConfig";
// SHARED with Inventory > Stock (src/inventory/stock/DataGrid.tsx "PDF" column).
import { buildInventoryLabelPdfViaStationeryV2 } from "../../inventory/stock/InventoryLabelPdfV2Builder";
import type { ItemList } from "../../inventory/types";
import type { ComponentListMode } from "./componentRecord";

/** A Components list that opens the record (componentRecord.COMPONENT_LIST_MODES). */
export type ComponentMode = ComponentListMode;

/** The record route, relative to a mode route (see the header comment). */
export const componentRecordPath = (id: number | string, from: ComponentMode) =>
  `../items/${id}?from=${from}`;

/** A list's row click opens the component record — unless the click was on a link in the
 * row (the Order # cell opens the order in a new tab). Shared by every Components list,
 * Components / Parts' item rows included (2026-10-07). */
export function useComponentRecordRowClick(mode: ComponentMode) {
  const navigate = useNavigate();
  return useCallback(
    (params: GridRowParams, event: MuiEvent<React.MouseEvent>) => {
      const target = event?.target as HTMLElement | undefined;
      if (target?.closest?.("a")) return;
      navigate(componentRecordPath(params.id, mode));
    },
    [mode, navigate]
  );
}

export function useComponentItemActions(mode: ComponentMode, canEdit: boolean) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const globalConfig = useGlobalConfig();
  const [labelLoading, setLabelLoading] = useState(false);
  const [labelBlob, setLabelBlob] = useState<Blob | null>(null);
  const [labelFilename, setLabelFilename] = useState("");

  const openRecord = useCallback(
    (id: number | string) => navigate(componentRecordPath(id, mode)),
    [mode, navigate]
  );

  const printLabel = useCallback(
    async (row: ItemList) => {
      setLabelLoading(true);
      try {
        const { blob, filename } = await buildInventoryLabelPdfViaStationeryV2({
          itemId: row.id,
          item: row,
          globalConfig: globalConfig ?? undefined,
        });
        setLabelBlob(blob);
        setLabelFilename(filename);
      } catch {
        enqueueSnackbar(`Failed to render the label for component ${row.id}`, {
          variant: "error",
        });
      } finally {
        setLabelLoading(false);
      }
    },
    [enqueueSnackbar, globalConfig]
  );

  const actions: ActionMenuItem[] = useMemo(
    () => [
      {
        // The record opens read-only without the parts Edit grant (legacy
        // opens it in preview: parts_display handler 1418-1450), so the entry
        // is always real — only its name changes.
        name: canEdit ? "Edit Component" : "View Component",
        requiresSelection: true,
        preventMultiSelect: true,
        multiSelectErrorMessage: "Select a single component.",
        isReturnDataSelection: true,
        onClick: (payload: any) => {
          const list = Array.from(payload?.selectedRows ?? []) as ItemList[];
          if (list.length === 0) {
            enqueueSnackbar("Select a row to continue", { variant: "warning" });
            return;
          }
          if (list.length > 1) {
            enqueueSnackbar("Select a single component.", { variant: "warning" });
            return;
          }
          openRecord(list[0].id);
        },
      },
      {
        name: "Print Label",
        requiresSelection: true,
        isReturnDataSelection: true,
        onClick: (payload: any) => {
          const list = Array.from(payload?.selectedRows ?? []) as ItemList[];
          if (list.length !== 1) {
            enqueueSnackbar(
              list.length === 0 ? "Select a row to continue" : "Select a single component.",
              { variant: "warning" }
            );
            return;
          }
          void printLabel(list[0]);
        },
      },
    ],
    [canEdit, enqueueSnackbar, openRecord, printLabel]
  );

  const onRowClick = useComponentRecordRowClick(mode);

  const labelPreview = (
    <>
      <LoadingSpinner open={labelLoading} />
      <PdfInlinePreviewDialog
        open={labelBlob !== null}
        blob={labelBlob}
        filename={labelFilename}
        title="Component Label Preview"
        onClose={() => {
          setLabelBlob(null);
          setLabelFilename("");
        }}
      />
    </>
  );

  return { actions, onRowClick, labelPreview };
}
